/**
 * The `/ai` reads and writes the conversation screen makes, over the generated client.
 *
 * ── The conversation is ONE cache entry, keyed by the plan ───────────────────────────────────
 * A new plan has a draft key the page minted (`draftKey`); the conversation is found by it and
 * created only when none exists, so a reload or a tab reopened later finds the same one (R12, R13).
 * Every turn the server answers with (a 202 from a send, a poll, a cancel, a resolution) is written
 * INTO that entry (`withTurn`), so the thread reads one value and never merges two sources.
 *
 * ── A running turn is watched, not re-rendered into existence ────────────────────────────────
 * `watchTurn` is `pages/exercise-library/lib/watchVideoProcessing.ts`'s construction: the generated
 * turn read, polled by `refetchInterval` through a `QueryObserver`, until the turn is no longer
 * queued or running. Each answer lands in the conversation entry; the terminal one re-reads the
 * conversation, because a finished ask can queue the outline itself (`parentTurnId`), and the
 * status, because every turn spends usage.
 */

import { QueryObserver, queryOptions, type QueryClient } from '@tanstack/react-query';

import {
  getAiConversations,
  getAiConversationsConversationId,
  getGetAiConversationsConversationIdTurnsTurnIdQueryOptions,
  getGetAiStatusQueryOptions,
  deleteAiConversationsConversationId,
  postAiConversations,
  postAiConversationsConversationIdMessages,
  postAiConversationsConversationIdImport,
  postAiConversationsConversationIdTurnsTurnIdApprove,
  postAiConversationsConversationIdTurnsTurnIdCancel,
  postAiConversationsConversationIdTurnsTurnIdDecisions,
  postAiConversationsConversationIdTurnsTurnIdFeedback,
  postAiConversationsConversationIdTurnsTurnIdResolutions,
  postAiConversationsConversationIdTurnsTurnIdRetry,
  postAiImportsSheets,
  putAiConversationsConversationIdTurnsTurnIdDraft,
} from '~/shared/api/generated/endpoints/ai/ai';
import type {
  TrainWithMeModulesAiDtosRequestsAiDraftSnapshotInput as SnapshotDto,
  TrainWithMeModulesAiDtosResponsesAiSpreadsheetResponse as SpreadsheetDto,
  TrainWithMeModulesAiDtosResponsesAiConversationResponse as ConversationDto,
  TrainWithMeModulesAiDtosResponsesAiTurnResponse as TurnDto,
  TrainWithMeModulesPlansDtosRequestsPostPlanTemplateRequest as PlanRequest,
} from '~/shared/api/generated/model';
import { ApiError } from '~/shared/api/mutator';
import type { ContentLanguage } from '~/shared/i18n/contentLanguage';

import type { AiChangeDecision, AiNamedDecision, AiIntent } from '../model/conversation';
import { turnFromWire } from '../model/fromWire';
import { aiRefusal, draftEditRefusal, turnPollDelayMs, type AiDraftEditRefusal, type AiRefusal } from '../model/refusals';
import { isTurnInFlight } from '../model/threadView';

export function aiStatusQueryOptions() {
  return getGetAiStatusQueryOptions();
}

/**
 * The conversation's cache entry, by its HANDLE: the draft key it was created under, or, for one
 * opened from the history, `conversationHandle(id)`. Every write and the turn watch take the same
 * handle, so they keep whichever entry the screen is reading.
 */
export function aiConversationQueryKey(handle: string) {
  return ['ai', 'conversation', 'draft', handle] as const;
}

/** The handle of a conversation opened by its id. `#` is in no draft key, so the two never collide. */
export function conversationHandle(conversationId: number): string {
  return `#${String(conversationId)}`;
}

/**
 * Keep the two cache entries one conversation can have saying the same thing.
 *
 * A conversation is read under its draft key (the one this device is in) and, opened from the
 * history, under its id (`conversationHandle`). Both may be cached at once, and a write that lands
 * in one must not leave the other showing an older state when the trainer steps back to it. So
 * every answer an entry takes is written into its sibling too, when the sibling is cached; and an
 * entry removed (a deleted conversation) takes its sibling with it. Returns the unsubscribe.
 */
export function mirrorConversationEntries(client: QueryClient): () => void {
  const cache = client.getQueryCache();
  // The writes below are events of their own, answered synchronously inside them. Structural
  // sharing keeps a sibling's OLD object when the new one is deep-equal, so two entries holding
  // equal but distinct objects would write each other back forever: the mirror's own writes are
  // never mirrored again.
  let mirroring = false;
  return cache.subscribe((event) => {
    if (mirroring) return;
    const [ai, kind, by, handle] = event.query.queryKey as readonly unknown[];
    if (ai !== 'ai' || kind !== 'conversation' || by !== 'draft' || typeof handle !== 'string') return;
    if (event.type === 'removed') {
      const gone = event.query.state.data as ConversationDto | undefined;
      if (gone === undefined) return;
      for (const sibling of siblingHandles(gone, handle)) client.removeQueries({ queryKey: aiConversationQueryKey(sibling), exact: true });
      return;
    }
    if (event.type !== 'updated' || event.action.type !== 'success') return;
    const data = event.query.state.data as ConversationDto | undefined;
    if (data === undefined) return;
    mirroring = true;
    try {
      for (const sibling of siblingHandles(data, handle)) {
        const key = aiConversationQueryKey(sibling);
        const held = client.getQueryData<ConversationDto>(key);
        if (held !== undefined && held !== data) client.setQueryData<ConversationDto>(key, data);
      }
    } finally {
      mirroring = false;
    }
  });
}

/** The other handles the same conversation is cached under. */
function siblingHandles(conversation: ConversationDto, handle: string): string[] {
  return [conversation.draftKey ?? null, conversationHandle(conversation.id)].filter(
    (each): each is string => each !== null && each !== '' && each !== handle,
  );
}

/** The handle of the conversation about a saved plan. `plan:` is in no draft key and no id handle. */
export function planConversationHandle(planTemplateId: number): string {
  return `plan:${String(planTemplateId)}`;
}

/**
 * The conversation about a saved library plan (R2, R13): found by the plan, created when it has none.
 * The server refuses a plan Buddy may not work on (another trainer's, or one assigned to a client).
 */
export function aiPlanConversationQueryOptions(planTemplateId: number, contentLanguage: ContentLanguage, create = true) {
  return queryOptions({
    queryKey: aiConversationQueryKey(planConversationHandle(planTemplateId)),
    queryFn: async ({ signal }) => {
      try {
        return await getAiConversations({ planTemplateId }, undefined, signal);
      } catch (error) {
        if (!create || !(error instanceof ApiError) || error.status !== 404) throw error;
        return postAiConversations({ planTemplateId, contentLanguage }, undefined, signal);
      }
    },
    staleTime: Infinity,
    refetchOnMount: 'always',
  });
}

/** A conversation from the history, read by its id. It exists, so nothing is created here. */
export function aiConversationByIdQueryOptions(conversationId: number) {
  return queryOptions({
    queryKey: aiConversationQueryKey(conversationHandle(conversationId)),
    queryFn: ({ signal }) => getAiConversationsConversationId(conversationId, undefined, signal),
    staleTime: Infinity,
    refetchOnMount: 'always',
  });
}

/**
 * The conversation under a draft key: found by it, created when there is none yet. A new one is
 * created FOR `customerId`, the trainer's client the plan is built for (or the library, `null`);
 * the recipient is set at creation and read back from the conversation ever after.
 *
 * `create: false` only finds: while Buddy is switched off or past its ceiling a conversation that
 * exists is still read, and none is started that nobody could write in. Its 404 is the answer.
 */
export function aiConversationQueryOptions(
  draftKey: string,
  contentLanguage: ContentLanguage,
  customerId: number | null,
  create = true,
) {
  return queryOptions({
    queryKey: aiConversationQueryKey(draftKey),
    queryFn: async ({ signal }) => {
      try {
        return await getAiConversations({ draftKey }, undefined, signal);
      } catch (error) {
        if (!create || !(error instanceof ApiError) || error.status !== 404) throw error;
        return postAiConversations(
          { draftKey, contentLanguage, ...(customerId === null ? {} : { customerId }) },
          undefined,
          signal,
        );
      }
    },
    // Fresh while the screen is open (the writes below keep the entry current themselves), and read
    // again every time the screen mounts: a draft saved or assigned elsewhere in this session bound
    // the conversation, and only a new read can show it.
    staleTime: Infinity,
    refetchOnMount: 'always',
  });
}

/** The wire turn read through the app's one rule (`isTurnInFlight`), so an unknown status is polled. */
function isActive(turn: TurnDto): boolean {
  return isTurnInFlight(turnFromWire(turn));
}

/** The conversation with `turn` in it: replaced where it stands, appended when it is new. */
export function withTurn(conversation: ConversationDto, turn: TurnDto): ConversationDto {
  const turns = conversation.turns ?? [];
  const known = turns.some((candidate) => candidate.id === turn.id);
  return {
    ...conversation,
    turns: known ? turns.map((candidate) => (candidate.id === turn.id ? turn : candidate)) : [...turns, turn],
    activeTurnId: isActive(turn) ? turn.id : conversation.activeTurnId === turn.id ? null : conversation.activeTurnId,
  };
}

/**
 * Put a turn the server answered with on screen.
 *
 * A turn still being worked on is merged as it is. A FINISHED one changes what the server waits on
 * (the outline to approve, the questions to answer, the outline that waits again after a cancel),
 * and that is the conversation's to say, not the turn's: so it is drawn from a fresh conversation
 * read, its card and its buttons in one paint, rather than merged first and corrected a moment
 * later. When that read fails the turn is merged after all, so the answer still shows, and the
 * conversation is left marked as behind: the screen reads it again until it lands
 * (`useBuddyConversation`), which is also what finds a turn the server queued behind this one.
 */
export async function showTurn(client: QueryClient, draftKey: string, turn: TurnDto): Promise<void> {
  const key = aiConversationQueryKey(draftKey);
  const merge = () => {
    client.setQueryData<ConversationDto>(key, (current) => (current === undefined ? current : withTurn(current, turn)));
  };
  if (isActive(turn)) {
    merge();
    return;
  }
  await client.refetchQueries({ queryKey: key, exact: true });
  const read = client.getQueryData<ConversationDto>(key);
  const landed = client.getQueryState(key)?.status === 'success' && read?.turns?.some((known) => known.id === turn.id && !isActive(known)) === true;
  if (landed) return;
  merge();
  await client.invalidateQueries({ queryKey: key, exact: true, refetchType: 'none' });
}

/** The turns the server is still working on: the ones the screen watches. */
export function activeTurnIds(conversation: ConversationDto | undefined): number[] {
  return (conversation?.turns ?? []).filter(isActive).map((turn) => turn.id);
}

/**
 * Poll one turn until it is finished, writing every answer into the conversation (the last one by
 * reading the conversation again, see below). Returns the stop
 * switch. A failed read ends this watch and says so (`onFailed`); the caller starts a new one after
 * a back-off while the conversation still names the turn as running, so one dropped poll never
 * leaves a build unwatched.
 */
export function watchTurn(
  client: QueryClient,
  draftKey: string,
  conversationId: number,
  turnId: number,
  onFailed: () => void,
): () => void {
  const turnQuery = getGetAiConversationsConversationIdTurnsTurnIdQueryOptions(conversationId, turnId);
  // A restarted watch starts from nothing: TanStack keeps a query in `error` while it still holds
  // data, and a fresh observer on that entry would read the old failure as its own first answer.
  client.removeQueries({ queryKey: turnQuery.queryKey, exact: true });
  let seenAt = -1;
  let lastMark = '';
  let unchanged = 0;
  const observer = new QueryObserver(client, {
    ...turnQuery,
    retry: false,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data !== undefined && !isActive(data)) return false;
      // The back-off counts answers that brought NOTHING new: a turn that moves (a new step, a new
      // log line) is asked about again at the shortest delay, so a build's lines and its finish
      // show as they happen; only a turn standing still is asked about less and less often.
      if (query.state.dataUpdatedAt !== seenAt) {
        seenAt = query.state.dataUpdatedAt;
        const mark = data === undefined ? '' : `${data.status ?? ''}|${data.step ?? ''}|${String(data.log?.length ?? 0)}`;
        unchanged = mark === lastMark ? unchanged + 1 : 0;
        lastMark = mark;
      }
      return turnPollDelayMs(unchanged);
    },
  });
  // The listener may run synchronously inside `subscribe()` itself, before the switch it returns
  // exists; a stop decided then is held and carried out the moment `subscribe()` returns.
  let stopped = false;
  let unsubscribe: (() => void) | null = null;
  const stop = () => {
    stopped = true;
    unsubscribe?.();
  };
  unsubscribe = observer.subscribe((result) => {
    if (stopped) return;
    // Only a read that has settled as failed ends the watch; `error` alone also stands while the
    // next refetch is already under way.
    if (result.isError && result.fetchStatus === 'idle') {
      stop();
      onFailed();
      return;
    }
    const turn = result.data;
    if (turn === undefined) return;
    void showTurn(client, draftKey, turn);
    if (isActive(turn)) return;
    stop();
    void client.invalidateQueries({ queryKey: aiStatusQueryOptions().queryKey });
  });
  if (stopped) unsubscribe();
  return stop;
}

/** A write's outcome: the turn it produced, or what its refusal means. */
export type AiWriteOutcome = { readonly outcome: 'sent'; readonly turn: TurnDto } | { readonly outcome: 'refused'; readonly refusal: AiRefusal };

async function write(run: () => Promise<TurnDto>): Promise<AiWriteOutcome> {
  try {
    return { outcome: 'sent', turn: await run() };
  } catch (error) {
    if (!(error instanceof ApiError)) return { outcome: 'refused', refusal: { kind: 'failed' } };
    return {
      outcome: 'refused',
      refusal: aiRefusal({ status: error.status, code: error.code, problem: error.problem }),
    };
  }
}

/**
 * The draft a message travels with once the conversation has one (R13): the plan as the trainer has
 * it, in the save request's shape, and its version; for a draft Buddy holds, which one and at which
 * revision.
 */
export type AiDraftSnapshot = SnapshotDto;

/**
 * Send one of the screen's intents: a message, answers, a skip, an approval or a retry. A message
 * about a draft carries `snapshot`; without one it is a message of a conversation that has none.
 */
export function sendIntent(conversationId: number, intent: AiIntent, snapshot: AiDraftSnapshot | null = null): Promise<AiWriteOutcome> {
  switch (intent.kind) {
    case 'message':
      return write(() =>
        postAiConversationsConversationIdMessages(conversationId, {
          text: intent.text,
          requestId: intent.requestId,
          ...(snapshot === null ? {} : { snapshot }),
          ...(intent.rejectedChangeId === undefined ? {} : { rejectedChangeId: intent.rejectedChangeId }),
        }),
      );
    case 'answers':
      return write(() =>
        postAiConversationsConversationIdMessages(conversationId, {
          answers: intent.answers.map((answer) =>
            answer.kind === 'option'
              ? { questionId: answer.questionId, optionId: answer.optionId }
              : { questionId: answer.questionId, text: answer.text },
          ),
          requestId: intent.requestId,
          ...(intent.text === undefined ? {} : { text: intent.text }),
        }),
      );
    case 'skipQuestions':
      return write(() => postAiConversationsConversationIdMessages(conversationId, { skipQuestions: true, requestId: intent.requestId }));
    case 'approveOutline':
      return write(() => postAiConversationsConversationIdTurnsTurnIdApprove(conversationId, intent.turnId));
    case 'retry':
      return write(() => postAiConversationsConversationIdTurnsTurnIdRetry(conversationId, intent.turnId));
  }
}

export function cancelTurn(conversationId: number, turnId: number): Promise<AiWriteOutcome> {
  return write(() => postAiConversationsConversationIdTurnsTurnIdCancel(conversationId, turnId));
}

/**
 * Unresolved names decided (R8), one or several in ONE call: „Dodaj wszystkie szybko" sends every
 * exercise it added together. The server remembers each pick for the trainer, and the route takes
 * at most 50 items, which is more names than a draft can carry.
 */
export function resolveExercises(
  conversationId: number,
  turnId: number,
  decisions: readonly AiNamedDecision[],
): Promise<AiWriteOutcome> {
  return write(() =>
    postAiConversationsConversationIdTurnsTurnIdResolutions(conversationId, turnId, {
      items: decisions.map(({ name, decision }) =>
        decision.kind === 'skip' ? { name, skip: true } : { name, exerciseId: decision.exerciseId },
      ),
    }),
  );
}

/**
 * The trainer's decisions on changes of a proposal (R53), against the draft version the proposal was
 * made on. Statistics only: nothing on the server changes a plan for them, and applying stays the
 * builder's button (R17).
 */
export function recordDecisions(
  conversationId: number,
  turnId: number,
  snapshotVersion: string,
  decisions: ReadonlyMap<string, AiChangeDecision>,
): Promise<AiWriteOutcome> {
  return write(() =>
    postAiConversationsConversationIdTurnsTurnIdDecisions(conversationId, turnId, {
      snapshotVersion,
      decisions: [...decisions].map(([changeId, decision]) => ({ changeId, decision })),
    }),
  );
}

/** The sheets of a spreadsheet, or why it is not read (R26, R27, R31). Calls no model, spends nothing. */
export type AiSheetListing =
  | { readonly outcome: 'listed'; readonly spreadsheet: SpreadsheetDto }
  | { readonly outcome: 'refused'; readonly refusal: AiRefusal };

export async function listSheets(file: File): Promise<AiSheetListing> {
  try {
    return { outcome: 'listed', spreadsheet: await postAiImportsSheets({ file }) };
  } catch (error) {
    if (!(error instanceof ApiError)) return { outcome: 'refused', refusal: { kind: 'failed' } };
    return { outcome: 'refused', refusal: aiRefusal({ status: error.status, code: error.code, problem: error.problem }) };
  }
}

/**
 * The chosen sheets of the same file imported into a draft (R27 to R31): queued as an `Import` turn.
 * The file is sent again; the server reads it inside the request and keeps none of it.
 */
export function importSpreadsheet(
  conversationId: number,
  file: File,
  sheets: readonly string[],
  schedulingMode: AiImportSchedulingMode | null,
  requestId: string,
): Promise<AiWriteOutcome> {
  return write(() =>
    postAiConversationsConversationIdImport(conversationId, {
      File: file,
      Sheets: [...sheets],
      RequestId: requestId,
      ...(schedulingMode === null ? {} : { SchedulingMode: schedulingMode }),
    }),
  );
}

/** How an imported plan runs when no chosen sheet names weekdays: the trainer's choice (R54). */
export type AiImportSchedulingMode = 'DateBased' | 'SelfPaced';

/** The trainer's rating of a draft (R47): voluntary, and nothing else is recorded. Answers whether it landed. */
export function rateTurn(conversationId: number, turnId: number, helpful: boolean, comment: string | null): Promise<boolean> {
  return postAiConversationsConversationIdTurnsTurnIdFeedback(conversationId, turnId, {
    helpful,
    ...(comment === null ? {} : { comment }),
  }).then(
    () => true,
    () => false,
  );
}

/** An edit's outcome: the turn with the recomposed draft, or why the edit was not taken. */
export type AiDraftEditOutcome =
  | { readonly outcome: 'saved'; readonly turn: TurnDto }
  | { readonly outcome: 'refused'; readonly refusal: AiDraftEditRefusal };

/**
 * Save the trainer's edit of a built draft: the whole plan, against the revision it was made on.
 * No model is called and no usage is spent; it works while Buddy is switched off. `keepalive` lets
 * the request outlive the page, for the one save sent as the page goes away.
 */
export async function editDraft(
  conversationId: number,
  turnId: number,
  plan: PlanRequest,
  revision: number,
  keepalive = false,
): Promise<AiDraftEditOutcome> {
  try {
    return {
      outcome: 'saved',
      turn: await putAiConversationsConversationIdTurnsTurnIdDraft(conversationId, turnId, { plan, revision }, { keepalive }),
    };
  } catch (error) {
    if (!(error instanceof ApiError)) return { outcome: 'refused', refusal: { kind: 'failed' } };
    return {
      outcome: 'refused',
      refusal: draftEditRefusal({
        status: error.status,
        code: error.code,
        problem: error.problem,
        retryAfterSeconds: error.retryAfterSeconds,
      }),
    };
  }
}

/** The trainer deletes the conversation (R45). Answers whether it is gone. */
export function deleteConversation(conversationId: number): Promise<boolean> {
  return deleteAiConversationsConversationId(conversationId).then(
    () => true,
    () => false,
  );
}
